import { useCallback } from 'react'

import {
  Icon,
  PageLimit,
  Pagination,
  Space,
  SubText,
  Text,
} from '@procurify/ui'

import { DataManagerSearch } from '@webapp/components/DataManager/components/DataManagerSearch'
import {
  configureColumn as col,
  DataTable,
  type RowCellMenuListItemProps,
} from '@webapp/components/DataTable'
import { type DatatableColumnConfig } from '@webapp/components/DataTable/types/DataTable.types'
import { UserAvatar } from '@webapp/components/UserAvatar'
import { useProcurifyIntl } from '@webapp/hooks'
import { type RepoPaginationResponse } from '@webapp/infrastructure/interfaces/repositories/RepoPaginationResponse'
import { type V1User } from '@webapp/platform/iam/entities/V1User'
import { SlackUserAsyncSelect } from '@webapp/platform/integrations/components/SlackUserAsyncSelect'
import { type SlackUser } from '@webapp/platform/integrations/entities/SlackUser'
import messages from './messages'

export interface IActiveUsersWithSlackTableViewProps {
  users: V1User[]
  pagination: RepoPaginationResponse
  isLoading: boolean
  page: number
  pageSize: number
  searchInputValue: string
  onPageChange: (page: number) => void
  onPageSizeChange: (pageSize: number) => void
  onSearchChange: (search: string) => void
  onSearchSubmit: (search: string) => void
  slackUserCache: Map<string, SlackUser>
  onUserLinkChange?: (userId: number, slackUser: SlackUser | null) => void
}

interface IProcurifyUserCellProps {
  data: V1User
}

const ProcurifyUserCell = ({ data }: IProcurifyUserCellProps) => (
  <Space direction='horizontal' size='small' align='center'>
    <UserAvatar
      name={data.fullName}
      userId={data.id}
      url={data.profileImage}
      size='small'
    />
    <Space size='xsmall'>
      <Text bold>{data.fullName}</Text>
      <SubText>{data.email}</SubText>
    </Space>
  </Space>
)

interface ISlackUserCellProps {
  data: V1User
  slackUserCache: Map<string, SlackUser>
  onUserLinkChange?: (userId: number, slackUser: SlackUser | null) => void
}

const SlackUserCell = ({
  data,
  slackUserCache,
  onUserLinkChange,
}: ISlackUserCellProps) => {
  if (!data.slackUserId) {
    return (
      <SlackUserAsyncSelect
        onSelect={(selected) => onUserLinkChange?.(data.id, selected)}
      />
    )
  }
  const slackUser = slackUserCache.get(data.slackUserId)
  if (!slackUser) {
    return <Text>{data.slackUserId}</Text>
  }
  return (
    <Space size='xsmall'>
      <Text bold>{slackUser.displayName}</Text>
      <SubText>{slackUser.email ?? slackUser.usernameWithAt}</SubText>
    </Space>
  )
}

export const ActiveUsersWithSlackTableView = ({
  users,
  pagination,
  isLoading,
  page,
  pageSize,
  searchInputValue,
  onPageChange,
  onPageSizeChange,
  onSearchChange,
  onSearchSubmit,
  slackUserCache,
  onUserLinkChange,
}: IActiveUsersWithSlackTableViewProps) => {
  const intl = useProcurifyIntl()

  const columns: DatatableColumnConfig<any, V1User>[] = [
    col('BASIC', {
      label: intl.formatMessage(messages.procurifyUserColumnHeader),
      getter: (data) => <ProcurifyUserCell data={data} />,
    }),
    col('BASIC', {
      label: '',
      width: 40,
      getter: () => (
        <Space align='center'>
          <Icon icon='pointer-right' />
        </Space>
      ),
    }),
    col('BASIC', {
      label: intl.formatMessage(messages.slackUserColumnHeader),
      getter: (data) => (
        <SlackUserCell
          data={data}
          slackUserCache={slackUserCache}
          onUserLinkChange={onUserLinkChange}
        />
      ),
    }),
  ]

  const getMenuListItems = useCallback(
    (data: V1User): RowCellMenuListItemProps[] => {
      if (!data.slackUserId) {
        return []
      }

      return [
        {
          icon: 'delete',
          text: intl.formatMessage(messages.unlinkUserAction),
          onClick: () => onUserLinkChange?.(data.id, null),
        },
      ]
    },
    [intl, onUserLinkChange]
  )

  const numPages = pagination.numPages || 1

  const handlePageLimitChange = ({ value }: { value: string }) => {
    onPageSizeChange(Number.parseInt(value, 10))
  }

  const pageLimitValue = {
    value: pageSize.toString(),
    label: pageSize.toString(),
  }

  return (
    <Space>
      <Space direction='horizontal' justify='space-between' align='center'>
        <DataManagerSearch
          value={searchInputValue}
          onChange={onSearchChange}
          onSearch={onSearchSubmit}
          placeholder={intl.formatMessage(messages.searchPlaceholder)}
        />
        <Pagination
          currentPage={page}
          numPages={numPages}
          onPageChange={onPageChange}
        />
      </Space>

      <DataTable<V1User>
        id='active-users-with-slack-table'
        aria-label={intl.formatMessage(messages.tableAriaLabel)}
        data={users}
        isLoading={isLoading}
        columns={columns}
        emptyStateText={intl.formatMessage(messages.emptyStateText)}
        customizableColumns={false}
        persistCustomizedColumns={false}
        menuListItems={onUserLinkChange ? getMenuListItems : undefined}
      />

      <Space direction='horizontal' justify='space-between' align='center'>
        <PageLimit value={pageLimitValue} onChange={handlePageLimitChange} />
        <Pagination
          currentPage={page}
          numPages={numPages}
          onPageChange={onPageChange}
        />
      </Space>
    </Space>
  )
}
